// src/components/dashboard/settings/ICDManagement.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  Chip,
} from "@mui/material";

// Core Stable MUI Icons
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

const ICDManagement = () => {
  // Live Dark/Light Mode Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Standard Pure English Clinical Records Dataset
  const [rows, setRows] = useState([
    {
      id: 13,
      name: "Intractable Epilepsy",
      description: "Epilepsy, unspecified, intractable, with status epilepticus",
      shortDescription: "Intractable Epilepsy w/ Status",
      code: "G40.911",
      createdBy: "awais09",
    },
    {
      id: 10,
      name: "Chronic Migraine",
      description: "Chronic migraine without aura, not intractable, without status migrainosus",
      shortDescription: "Chronic Migraine w/o Aura",
      code: "G43.709",
      createdBy: "Awais09",
    },
    {
      id: 9,
      name: "Parkinson's Disease",
      description: "Idiopathic primary Parkinson's disease with resting tremors",
      shortDescription: "Primary Parkinsonism",
      code: "G20.A1",
      createdBy: "Awais09",
    },
    {
      id: 1,
      name: "Essential Hypertension",
      description: "Essential primary systemic hypertension with clinical risk",
      shortDescription: "Primary Hypertension",
      code: "I10.9",
      createdBy: "awais09",
    },
    {
      id: 14,
      name: "Cerebral Infarction (Stroke)",
      description: "Acute ischemic cerebral infarction affecting middle cerebral artery",
      shortDescription: "Acute Ischemic Stroke",
      code: "I63.9",
      createdBy: "Dr. Usman",
    },
    {
      id: 15,
      name: "Peripheral Neuropathy",
      description: "Diabetic distal symmetric polyneuropathy of lower extremities",
      shortDescription: "Diabetic Neuropathy",
      code: "E11.40",
      createdBy: "Dr. Bilal",
    },
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [openModal, setOpenModal] = useState(false);

  // New Item Modal Form State
  const [newEntry, setNewEntry] = useState({
    name: "",
    description: "",
    shortDescription: "",
    code: "",
    createdBy: "awais09",
  });

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";

  const handleDelete = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleCreate = () => {
    if (!newEntry.name.trim() || !newEntry.code.trim()) return;
    const nextId = Math.max(...rows.map((r) => r.id), 0) + 1;
    setRows([{ id: nextId, ...newEntry }, ...rows]);
    setNewEntry({
      name: "",
      description: "",
      shortDescription: "",
      code: "",
      createdBy: "awais09",
    });
    setOpenModal(false);
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & ACTION BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AddBoxRoundedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                Diagnosis Management
              </Typography>
              <Chip
                label="ICD-10 Dataset"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Manage clinical ICD-10 diagnostic codes, descriptions, and catalog entries
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          startIcon={<AddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            height: "40px",
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.82rem",
            borderRadius: "6px",
            px: 2.5,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#046337",
              boxShadow: "none",
            },
          }}
        >
          Add Diagnosis
        </Button>
      </Box>

      {/* 2. DIAGNOSIS DATA TABLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "620px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "90px" }}>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Short Description</TableCell>
                <TableCell>ICD Code</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="right" sx={{ width: "90px", pr: 3 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                      No diagnostic codes recorded
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 1.4,
                        },
                      }}
                    >
                      {/* ID Badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {row.id}
                        </Box>
                      </TableCell>

                      {/* Name */}
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.86rem" }}>
                        {row.name}
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem" }}>
                        {row.description}
                      </TableCell>

                      {/* Short Description */}
                      <TableCell sx={{ color: primaryTextColor, fontSize: "0.82rem", fontWeight: 500 }}>
                        {row.shortDescription}
                      </TableCell>

                      {/* Code */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: tealButton,
                            fontSize: "0.84rem",
                          }}
                        >
                          {row.code}
                        </Typography>
                      </TableCell>

                      {/* Created By */}
                      <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem", fontWeight: 600 }}>
                        {row.createdBy}
                      </TableCell>

                      {/* Action (Delete) */}
                      <TableCell align="right" sx={{ pr: 2.5 }}>
                        <Tooltip title="Delete Diagnosis">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                            sx={{
                              color: "#EF4444",
                              p: 0.6,
                              borderRadius: "6px",
                              "&:hover": {
                                backgroundColor: "rgba(239, 68, 68, 0.08)",
                              },
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>

      {/* 3. ADD DIAGNOSIS MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1.5px solid ${cardBorder}`,
            color: primaryTextColor,
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.15rem", color: primaryTextColor, pb: 1 }}>
          Add Diagnostic Code
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Diagnosis Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Trigeminal Neuralgia"
                value={newEntry.name}
                onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Diagnosis Code (ICD-10)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. G50.0"
                value={newEntry.code}
                onChange={(e) => setNewEntry({ ...newEntry, code: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Short Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Facial Neuralgia"
                value={newEntry.shortDescription}
                onChange={(e) => setNewEntry({ ...newEntry, shortDescription: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Created By
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. awais09 or Dr. Usman"
                value={newEntry.createdBy}
                onChange={(e) => setNewEntry({ ...newEntry, createdBy: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Full Clinical Description
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="e.g. Trigeminal neuralgia, unspecified paroxysmal facial nerve pain"
              value={newEntry.description}
              onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{
              color: secondaryTextColor,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: addGreen,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 2.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Save Diagnosis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ICDManagement;